<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Ref } from '@/helpers/Decorators';
import { KeyValuePair, Option } from '@/helpers/Interfaces';
import { Form } from '@/helpers/Form';
import { $t } from '@/plugins/localization';
import { BpmnEmitOptions } from '@/modules/low-code/process/services/BpmnService';
import { FrontActionModal } from '@/components/common/dynamic-grid/helpers/GridEnums';
import ProcessService, { ChangeDocumentDataModel, FieldInfo } from '@/modules/low-code/process/services/ProcessService';
import IdeoModal from '@/components/ideo/modal/IdeoModal.vue';

@Options({
    emits: ['reload'],
})
export default class ChangeDocumentDataModal extends Vue
{
    @Ref('modal') public modal: () => InstanceType<typeof IdeoModal>;

    public documentFields: KeyValuePair[] = [];
    public selectedField: KeyValuePair = null;
    public documentData: Record<string, FieldInfo> = {};

    public form = Form.create<ChangeDocumentDataModel>({
        documentData: {}
    });

    public action = Form.create<BpmnEmitOptions>({
        id: '',
        licence: '',
        actionName: '',
        buttonId: '',
        displayMode: 'Modal',
        displaySize: 'Medium',
        title: '',
        buttonName: $t('[[[Edycja pól dokumentu]]]'),
        buttonVariant: 'success',
        buttonIcon: '',
        groupAction: false,
    });

    public mounted(): void
    {
        this.$events.$on(FrontActionModal.ChangeDocumentDataModal, async (action: BpmnEmitOptions) =>
        {
            this.action.withData(action);
            await this.loadDetails();
            this.modal().show();
        });
    }

    public unmounted(): void
    {
        this.$events.$off(FrontActionModal.ChangeDocumentDataModal);
    }

    public async accept(): Promise<void>
    {
        try
        {
            this.form.wait();

            await ProcessService.updateDocumentData(this.action.licence, this.action.id, this.form.formatData());
            this.$alert.success(this.$t('[[[Akcja wykonana pomyślnie]]]'));
            this.modal().hide();

            this.selectedField = null;
            this.documentData = null;

            setTimeout(() =>
            {
                this.form.reset();
                this.$emit('reload');
                this.$events.$emit('refetchActions');
            }, 500);
            this.form.clear();
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
                422: (ex: any) => [
                    this.form.$errors.record(ex.data.errors),
                    this.$alert.warning(this.$t('[[[Nie wszystkie pola zostały wypełnione prawidłowo.]]]')),
                ],
            });
        }
        finally
        {
            this.form.continue();
        }
    }

    public async loadDetails(): Promise<boolean>
    {
        if (!this.action.id) return false;

        try
        {
            const response = await ProcessService.getDocumentData(this.action.licence, this.action.id);

            this.documentData = response;

            this.documentFields = Object.entries(response).map(p =>
            {
                return { key: p[0], value: p[1].name };
            });
        }
        catch (ex)
        {
            return false;
        }

        return true;
    }

    public onFieldChanged(): void
    {
        this.form.documentData = {};
        this.form.documentData[this.selectedField.key] = this.documentData[this.selectedField.key].data;
    }
}
</script>

<template>
    <IdeoModal ref="modal" :title="action.title" centered>
        <template #default>
            <IdeoAlert variant="warning" :show="true" class="mb-3">
                Wprowadzenie poniższych zmian w obiegu dokumentu może mieć wpływ na spójność danych przeliczonych na tym dokumencie lub dokumentach powiązanych.
            </IdeoAlert>
            <IdeoFormGroup
                :label="$t('[[[Pole]]]')"
                :invalid-feedback="form.$errors.first('field')"
                :state="form.$errors.state('field')"
                required
            >
                <IdeoSelect v-model="selectedField" :options="documentFields" @update:modelValue="onFieldChanged" />
            </IdeoFormGroup>
            <IdeoFormGroup
                v-if="selectedField"
                :label="$t('[[[Wartość]]]')"
                required
            >
                <IdeoFormInput v-model="form.documentData[selectedField.key]" type="text" />
            </IdeoFormGroup>
        </template>
        <template #modal-footer="{ cancel }">
            <ActionButton
                name="btnConfirmAccept"
                variant="success"
                :text="$t('[[[Zapisz]]]')"
                :confirm="$t('[[[Wprowadzenie  zmian może spowodować utratę spójności jeśli dane z tego dokumentu lub dokumentów powiązanych są przeliczane w trakcie procesowania. Czy na pewno wprowadzić zmiany?]]]')"
                @click="accept"
            >
            </ActionButton>
            <IdeoButton variant="secondary" @click="cancel">
                {{ $t('[[[Anuluj]]]') }}
            </IdeoButton>
        </template>
    </IdeoModal>
</template>
